import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Download, ChevronLeft, ChevronRight, Bell, Circle, CircleDot, CircleCheckBig, X, Filter, ArrowUpDown } from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { PageViewOptionsBar } from "@/components/navigation/PageViewOptionsBar/PageViewOptionsBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TaskResultsSummary from "@/components/tasks/results/TaskResultsSummary/TaskResultsSummary";
import TaskStatusDivider, {
  shouldShowStatusDivider,
  statusCount,
  sortTasksByStatus,
} from "@/components/tasks/lists/TaskStatusDivider/TaskStatusDivider";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import {
  formatDateKey,
  formatDateLong,
  formatDateTimeDisplay,
  formatDurationShort,
  formatTimeDisplay,
  parseDateKey,
} from "@/lib/time";
import { toast } from "sonner";
import { deriveTaskLifecycle, getTaskLifecycleLabel } from "@shared/taskLifecycle";

function shiftDate(key: string, deltaDays: number) {
  const d = parseDateKey(key);
  d.setDate(d.getDate() + deltaDays);
  return formatDateKey(d);
}

export default function Reports() {
  const [, setLocation] = useLocation();
  const [date, setDate] = useState(() => formatDateKey(new Date()));
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const reportQuery = trpc.reports.daily.useQuery({ date });

  const grouped = useMemo(() => {
    const tasks = reportQuery.data?.tasks ?? [];
    const map = new Map<string, typeof tasks>();
    for (const t of tasks) {
      const key = `${t.listId ?? "none"}::${t.listName ?? "Inbox"}::${t.listColor ?? DEFAULT_AREA_COLOR}`;
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([key, ts]) => {
      const [, name, color] = key.split("::");
      return { listName: name, listColor: color ?? DEFAULT_AREA_COLOR, tasks: sortTasksByStatus(ts) };
    });
  }, [reportQuery.data]);

  function exportText() {
    const r = reportQuery.data;
    if (!r) return;
    const lines: string[] = [];
    lines.push(`DAILY SUMMARY — ${formatDateLong(parseDateKey(r.date))}`);
    lines.push(``);
    lines.push(`Completed: ${r.done}    Started: ${r.doing}    Skipped: ${r.skipped}    Pending: ${r.pending}    Total: ${r.total}`);
    lines.push(``);
    for (const g of grouped) {
      lines.push(`── ${g.listName.toUpperCase()} ──`);
      for (const t of g.tasks) {
        const lifecycle = deriveTaskLifecycle(t);
        const status = lifecycle === "done"
          ? "[DONE]"
          : lifecycle === "doing"
            ? "[STARTED]"
            : lifecycle === "skipped"
              ? "[SKIP]"
              : "[ -- ]";
        const at =
          lifecycle === "done" && t.completedAt
            ? ` (at ${formatDateTimeDisplay(new Date(t.completedAt))})`
            : "";
        lines.push(`${status} ${t.startTime ?? "--"}  ${t.title}${at}`);
        if (t.details) lines.push(`        details: ${t.details}`);
        if (t.notes) lines.push(`        notes:   ${t.notes}`);
      }
      lines.push(``);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily-groove-${r.date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Summary exported");
  }

  function toggleGroup(key: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const r = reportQuery.data;

  return (
    <AppShell title="Reports">
      <div className="container max-w-3xl py-8 space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-4xl tracking-tight">
            Daily Report
          </h1>
          <p className="text-sm text-muted-foreground max-w-prose">
            A persistent record of what got done, what was skipped, and any notes captured along the way.
          </p>
        </header>

        <PageViewOptionsBar
          leftSlot={
            <button
              onClick={() => setFilterOpen(true)}
              className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              title="Filter"
            >
              <Filter className="w-4 h-4" />
            </button>
          }
          rightSlot={
            <button
              onClick={() => setSortOpen(true)}
              className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              title="Sort"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          }
        />

        <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Filter</DialogTitle>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        <Dialog open={sortOpen} onOpenChange={setSortOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sort</DialogTitle>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        {/* Date picker bar */}
        <div className="flex items-center justify-between bg-card border border-border/60 rounded-xl px-3 py-3">
          <button
            onClick={() => setDate(shiftDate(date, -1))}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            aria-label="Previous day"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-center">
            <div className="font-display text-lg">{formatDateLong(parseDateKey(date))}</div>
            <button
              onClick={() => setDate(formatDateKey(new Date()))}
              className="text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            >
              jump to today
            </button>
          </div>
          <button
            onClick={() => setDate(shiftDate(date, 1))}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            aria-label="Next day"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Summary stats */}
        {r && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Total" value={r.total} />
            <Stat label="Done" value={r.done} accent="done" />
            <Stat label="Started" value={r.doing} />
            <Stat label="Skipped" value={r.skipped} accent="skipped" />
            <Stat label="Pending" value={r.pending} />
          </div>
        )}

        {/* Export button */}
        {r && r.total > 0 && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={exportText} className="gap-1.5">
              <Download className="h-4 w-4" />
              Export summary
            </Button>
          </div>
        )}

        {/* Grouped by list */}
        {reportQuery.isLoading ? (
          <div className="space-y-2">
            {[0, 1].map(i => (
              <div key={i} className="h-32 rounded-xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : !r || r.total === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-16 text-center space-y-2">
            <p className="font-display text-lg">No tasks scheduled for this day</p>
            <p className="text-sm text-muted-foreground">
              Schedule tasks from the calendar to see them here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(g => {
              const headerC = resolveTaskColors({
                barColor: null,
                bgColor: null,
                listColor: g.listColor,
                listBgColor: null,
              });
              const doneCount = g.tasks.filter(t => deriveTaskLifecycle(t) === "done").length;
              return (
                <section key={g.listName} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: headerC.bar }}
                    />
                    <h2 className="font-display text-lg tracking-tight">
                      {g.listName}
                    </h2>
                    <span className="text-xs text-muted-foreground font-mono">
                      · {doneCount} of {g.tasks.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {g.tasks.map((t, index) => {
                      const c = resolveTaskColors({
                        barColor: t.barColor,
                        bgColor: t.bgColor,
                        listColor: t.listColor ?? DEFAULT_AREA_COLOR,
                        listBgColor: t.listBgColor,
                      });
                      const lifecycle = deriveTaskLifecycle(t);
                      const isDone = lifecycle === "done";
                      const isDoing = lifecycle === "doing";
                      const isSkipped = lifecycle === "skipped";
                      const isPending = lifecycle === "pending";
                      const groupKey = `${g.listName}:${lifecycle}`;
                      const collapsed = collapsedGroups.has(groupKey);
                      return (
                        <div key={t.id} className="space-y-2">
                          {shouldShowStatusDivider(g.tasks, index) && (
                            <TaskStatusDivider
                              status={lifecycle}
                              count={statusCount(g.tasks, lifecycle)}
                              collapsed={collapsed}
                              onToggle={() => toggleGroup(groupKey)}
                            />
                          )}
                          {!collapsed && (
                          <div
                            className="group relative border rounded-xl overflow-hidden transition-all border-border/60 hover:border-border"
                            style={{ background: c.soft }}
                          >
                            <div className="flex items-stretch">
                              <span
                                className="w-1.5 shrink-0"
                                style={{ background: c.bar }}
                                aria-hidden
                              />
                              <button
                                onClick={() => setLocation(`/task/${t.id}`)}
                                className="flex-1 min-w-0 text-left px-4 py-3"
                              >
                              {/* Row 1: time (bold) · list name (light) */}
                              <div className="flex justify-between items-center gap-2">
                                <span className="ui-time flex items-center gap-2">
                                  {t.alertEnabled && (
                                    <Bell
                                      className="h-3 w-3 shrink-0"
                                      style={{ color: c.ink }}
                                      aria-label="Alert on"
                                    />
                                  )}
                                  {t.startMinutes !== null && (
                                    <span
                                      className="text-xs font-semibold tabular-nums font-mono shrink-0"
                                      style={{ color: c.ink }}
                                    >
                                      {formatTimeDisplay(t.startMinutes)}
                                    </span>
                                  )}
                                  {t.durationMin && (
                                    <span
                                      className="text-xs tabular-nums font-mono shrink-0"
                                      style={{ color: c.ink, opacity: 0.7 }}
                                    >
                                      {formatDurationShort(t.durationMin)}
                                    </span>
                                  )}
                                </span>
                                <span
                                  className="text-[11px] uppercase tracking-[0.14em] truncate"
                                  style={{ color: c.ink, opacity: 0.55 }}
                                >
                                  {t.listName}
                                </span>
                              </div>

                              {/* Row 2: title (bold) */}
                              <div
                                className={`flex items-center gap-1.5 font-semibold text-base mt-1 ${isSkipped ? "line-through" : ""}`}
                                style={{ color: c.ink }}
                              >
                                {isDone && (
                                  <CircleCheckBig className="h-3.5 w-3.5 shrink-0" aria-label="Done" />
                                )}
                                {isDoing && (
                                  <CircleDot className="h-3.5 w-3.5 shrink-0" aria-label={getTaskLifecycleLabel(lifecycle)} />
                                )}
                                {isSkipped && (
                                  <X className="h-3.5 w-3.5 shrink-0" aria-label="Skipped" />
                                )}
                                {isPending && (
                                  <Circle className="h-3.5 w-3.5 shrink-0" aria-label="Pending" />
                                )}
                                <span className="min-w-0 truncate">{t.title}</span>
                              </div>

                              {/* Description */}
                              {t.details && (
                                <div
                                  className="text-sm mt-1 truncate"
                                  style={{ color: c.ink, opacity: 0.75 }}
                                >
                                  {t.details}
                                </div>
                              )}


                              <TaskResultsSummary
                                status={t.status}
                                startedAt={t.startedAt}
                                completedAt={t.completedAt}
                                notes={t.notes}
                                color={c.ink}
                                notesDisplay="text"
                                className="mt-2"
                              />
                              </button>
                            </div>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "done" | "skipped";
}) {
  return (
    <div className="bg-card border border-border/60 rounded-xl p-4 text-center">
      <div className="font-display text-3xl tabular-nums font-mono">{value}</div>
      <div
        className={`text-[10px] uppercase tracking-wider mt-1 ${
          accent === "done"
            ? "text-foreground"
            : accent === "skipped"
              ? "text-muted-foreground"
              : "text-muted-foreground"
        }`}
      >
        {label}
      </div>
    </div>
  );
}
